import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";
import { ColDef } from "ag-grid-community";
import { Copy, Download, Eye, Search } from "lucide-react";

import PaginationLayout from "../components/PaginationLayout";
import profile_placeholder from "../assets/images/profile_placeholder.png";
import henceforthApi from "../utils/henceforthApi";
import { GlobalContext } from "../context/Provider";
import DownloadfileModal from "../components/common/downloadfileModal";

import AgGridTable from "../components/ui/AgGridTable";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { formatPhone } from "../utils/phone";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

type UserData = {
  _id: string;
  profile_pic: string;
  name: string;
  created_at: string;
  phone_no: number;
  country_code?: string;
  email: string;
  document_status: string;
  passenger_document_status: string;
  driver_document_status: string;
  country: string;
};

export default () => {
  const navigate = useNavigate();
  const location = useLocation();
  const limit = 10;
  const { authState, handleFilter, handlePagination, handleError } =
    useContext(GlobalContext);
  const params: any = useParams();
  const newParam = new URLSearchParams(location.search);

  const [loading, setLoading] = useState<boolean>(false);
  const [pageData, setPageData] = useState<Array<UserData>>([]);
  const [totalCount, setTotalCount] = useState(0);

  if (authState.access_token) {
    henceforthApi.setToken(authState.access_token);
  }

  const downloadCsv = async (start_date: number, end_date: number) => {
    try {
      let apiRes = await henceforthApi.User.export(
        moment(start_date).format("DD/MM/YYYY"),
        moment(end_date).format("DD/MM/YYYY")
      );
      let data = apiRes.data.data;

      const rows = [["Sr No.", "Name", "Email", "Date", "Phone No."]];
      if (Array.isArray(data)) {
        data.forEach((res: any, index: number) => {
          rows.push([
            index + 1,
            res.name,
            res.email,
            moment(Number(res.created_at)).format("DD MMM YYYY"),
            formatPhone(res.phone_no, res.country_code),
          ]);
        });
      }

      let csvContent =
        "data:text/csv;charset=utf-8," +
        rows.map((e) => e.join(",")).join("\n");
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `user_${moment().valueOf()}.csv`);
      document.body.appendChild(link);
      link.click();

      const closeModal = document.getElementById("closeModal");
      if (closeModal) {
        closeModal.click();
      }
    } catch (error: any) {
      handleError(error);
    }
  };

  const q = newParam.toString();

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        let apiRes = await henceforthApi.User.getListing(
          q,
          Number(params.page) - 1,
          limit
        );
        setTotalCount(apiRes.data.total_count);
        setPageData(apiRes.data.data || []);
        setLoading(false);
      } catch (error: any) {
        setLoading(false);
        setPageData([]);
        handleError(error);
        setTotalCount(0);
      }
    };
    init();
  }, [
    params.page,
    newParam.get("search"),
    newParam.get("document_verified"),
    newParam.get("country"),
  ]);

  const handleUserFilter = (name: string, value: string) => {
    if (value && value !== "ALL") {
      newParam.set(name, value);
    } else {
      newParam.delete(name);
    }
    navigate({ pathname: "/users/1", search: newParam.toString() });
  };

  const copyEmail = (email: string) => {
    if (!email) return;
    navigator.clipboard
      .writeText(email)
      .then(() => toast.success("Email copied to clipboard"))
      .catch(() => toast.error("Failed to copy email"));
  };

  // AG Grid Column Definitions with optimal proportions
  const columnDefs = useMemo<ColDef[]>(() => {
    const pageNum = Number(params.page) || 1;
    return [
      {
        headerName: "Sr No.",
        minWidth: 70,
        maxWidth: 80,
        width: 75,
        valueGetter: (p) => {
          return (pageNum - 1) * limit + (p.node?.rowIndex ?? 0) + 1;
        },
        cellClass: "font-medium text-slate-500",
      },
      {
        headerName: "User",
        field: "name",
        minWidth: 150,
        flex: 1,
        cellRenderer: (p: any) => {
          const user = p.data as UserData;
          const imageSrc =
            user?.profile_pic && user?.profile_pic !== "null"
              ? henceforthApi.FILES.imageOriginal(user.profile_pic)
              : profile_placeholder;
          return (
            <div className="flex items-center gap-2.5 py-1">
              <img
                src={imageSrc}
                alt={user?.name || "User"}
                className="w-8 h-8 rounded-full object-cover border border-slate-200 shrink-0"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = profile_placeholder;
                }}
              />
              <div className="flex flex-col truncate">
                <span className="font-semibold text-slate-800 text-xs leading-tight hover:text-[#006747] transition-colors truncate">
                  {user?.name || "Not Available"}
                </span>
                <span className="text-[11px] text-slate-400">
                  {user?.country || "N/A"}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        headerName: "Email",
        field: "email",
        minWidth: 170,
        flex: 1.1,
        cellRenderer: (p: any) => {
          const email = p.value;
          if (!email) return <span className="text-slate-400 text-xs">Not Available</span>;
          return (
            <div
              onClick={() => copyEmail(email)}
              title="Click to copy email"
              className="group flex items-center gap-1.5 cursor-pointer text-slate-700 hover:text-[#006747] transition-colors text-xs"
            >
              <span className="truncate">{email}</span>
              <Copy className="w-3 h-3 text-slate-400 group-hover:text-[#006747] opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
            </div>
          );
        },
      },
      {
        headerName: "Phone No.",
        field: "phone_no",
        minWidth: 130,
        width: 150,
        valueFormatter: (p) => formatPhone(p.value, p.data?.country_code),
      },
      {
        headerName: "Registered Date",
        field: "created_at",
        minWidth: 150,
        width: 155,
        valueFormatter: (p) => {
          return p.value ? moment(Number(p.value)).format("DD MMM YYYY") : "N/A";
        },
      },
      {
        headerName: "Driver Status",
        field: "driver_document_status",
        minWidth: 135,
        width: 140,
        cellRenderer: (p: any) => {
          const status = p.value;
          if (!status || status === "NOT_VERIFY" || status === "NOT_VERIFIED") {
            return <Badge variant="secondary">NOT VERIFIED</Badge>;
          }
          if (status === "ACCEPTED") {
            return <Badge variant="success">ACCEPTED</Badge>;
          }
          if (status === "PENDING") {
            return <Badge variant="warning">PENDING</Badge>;
          }
          if (status === "REJECTED") {
            return <Badge variant="destructive">REJECTED</Badge>;
          }
          return <Badge variant="outline">{status.replace(/_/g, " ")}</Badge>;
        },
      },
      {
        headerName: "Actions",
        minWidth: 70,
        maxWidth: 85,
        width: 75,
        sortable: false,
        filter: false,
        cellRenderer: (p: any) => {
          const user = p.data as UserData;
          return (
            <div className="flex items-center justify-center w-full">
              <Link
                to={`/user/${user?._id}`}
                title="View Details"
                className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-[#006747] hover:bg-[#006747]/10 transition-colors"
              >
                <Eye className="w-4 h-4" />
              </Link>
            </div>
          );
        },
      },
    ];
  }, [params.page]);

  const selectedVerification = newParam.get("document_verified") || "ALL";

  return (
    <>
      {/* Compact Single Header Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        {/* Left: Search Input */}
        <div className="relative w-64 sm:w-72">
          <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input
            name="search"
            defaultValue={newParam.get("search") || ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              handleFilter(e.target.name, e.target.value);
            }}
            placeholder="Search name or email..."
            className="search-icon-input h-9 text-sm bg-white border-slate-200 focus:bg-white shadow-2xs"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          {/* Status Filter */}
          <div className="w-40">
            <Select
              value={selectedVerification}
              onValueChange={(val) => handleUserFilter("document_verified", val)}
            >
              <SelectTrigger className="h-9 text-sm bg-white shadow-2xs">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Status</SelectItem>
                <SelectItem value="0">Pending</SelectItem>
                <SelectItem value="2">Accepted</SelectItem>
                <SelectItem value="1">Rejected</SelectItem>
                <SelectItem value="3">Not Verified</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Export Button */}
          <Button
            variant="default"
            size="sm"
            data-bs-toggle="modal"
            data-bs-target="#exampleModal"
            className="h-9 px-4 text-sm gap-2 shadow-xs"
          >
            <Download className="w-4 h-4" />
            <span>Export</span>
          </Button>
        </div>
      </div>

      {/* AG Grid Table */}
      <AgGridTable
        rowData={pageData}
        columnDefs={columnDefs}
        loading={loading}
        domLayout="autoHeight"
        emptyMessage="No users found for this filter"
      />

      <div className="mt-3">
        <PaginationLayout
          count={totalCount}
          data={pageData}
          page={Number(params.page)}
          limit={Number(limit)}
          loading={loading}
          onPageChange={(val) => handlePagination(val)}
        />
      </div>

      <DownloadfileModal data={downloadCsv} />
    </>
  );
};

